import {
  HUMAN_ROLE_IDS,
  createCorrelationId,
  normalizeStudioQuery,
  validateCustomizationReason,
  type HumanRoleId,
  type RolePresetOption,
  type StudioUser,
} from '../../domain/customization/studio'
import { customizationStudioProvider } from '../../integrations/supabase/customizationStudioProvider'
import { mapProviderError, type AppResult } from '../platform/public'

function isHumanRole(value: unknown): value is HumanRoleId {
  return typeof value === 'string' && HUMAN_ROLE_IDS.includes(value as HumanRoleId)
}

export const customizationApi = {
  async searchUsers(tenantId: string, query: string): Promise<AppResult<StudioUser[]>> {
    const result = await customizationStudioProvider.searchUsers(tenantId, normalizeStudioQuery(query))
    if (result.error) return { data: null, error: mapProviderError(result.error) }
    const rows = (result.data ?? []) as Array<Record<string, unknown>>
    return {
      data: rows.map((row) => ({
        userId: String(row.user_id),
        username: typeof row.username === 'string' ? row.username : null,
        displayName: typeof row.display_name === 'string' ? row.display_name : null,
        legacyRole: String(row.legacy_role ?? ''),
        humanRoleId: isHumanRole(row.human_role_id) ? row.human_role_id : null,
        rolePresetVersion: typeof row.role_preset_version === 'number' ? row.role_preset_version : null,
        membershipStatus: String(row.membership_status ?? ''),
        defaultView: typeof row.default_view === 'string' ? row.default_view : null,
        hasUserOverride: row.has_user_override === true,
        enabledModules: Array.isArray(row.enabled_modules) ? row.enabled_modules.map(String) : [],
      })),
      error: null,
    }
  },

  async listRolePresets(): Promise<AppResult<RolePresetOption[]>> {
    const result = await customizationStudioProvider.listRolePresets()
    if (result.error || !result.data) return { data: null, error: mapProviderError(result.error) }
    const presets = result.data.presets as Array<{ role_id: string; version: number }>
    const assignments = result.data.assignments as Array<{ role_id: string; preset_version: number; capability_id: string }>
    return {
      data: presets.filter((preset) => isHumanRole(preset.role_id)).map((preset) => ({
        roleId: preset.role_id as HumanRoleId,
        version: preset.version,
        capabilities: assignments
          .filter((item) => item.role_id === preset.role_id && item.preset_version === preset.version)
          .map((item) => item.capability_id),
      })),
      error: null,
    }
  },

  async assignRole(input: { tenantId: string; userId: string; roleId: HumanRoleId; presetVersion: number; reason: string }): Promise<AppResult<null>> {
    try {
      const result = await customizationStudioProvider.assignRole({
        ...input,
        reason: validateCustomizationReason(input.reason),
        effectiveAt: new Date().toISOString(),
        correlationId: createCorrelationId('studio-role'),
      })
      return { data: null, error: mapProviderError(result.error) }
    } catch (error) {
      return { data: null, error: { code: 'INVALID', message: error instanceof Error ? error.message : 'Invalid customization request' } }
    }
  },

  async activateDefaultViewOverride(input: { tenantId: string; userId: string; defaultView: string; reason: string }): Promise<AppResult<unknown>> {
    try {
      const result = await customizationStudioProvider.activateUserOverride({
        tenantId: input.tenantId,
        userId: input.userId,
        payload: { defaultView: input.defaultView.trim() },
        reason: validateCustomizationReason(input.reason),
        correlationId: createCorrelationId('studio-config'),
      })
      return { data: result.data, error: mapProviderError(result.error) }
    } catch (error) {
      return { data: null, error: { code: 'INVALID', message: error instanceof Error ? error.message : 'Invalid customization request' } }
    }
  },
}
